<template>
  <div class="mb-15">
    <v-breadcrumbs :items="breadcrumbs"></v-breadcrumbs>
    <h1>{{ $t('classDiary.createClassDiary') }}</h1>

    <v-row>
      <v-col cols="6">
        <v-select
          v-model="classId"
          :items="classes"
          :item-title="(item) => item.classId === undefined ? null : `(${item.year}) ${item.className}`" 
          item-value="classId"
          variant="outlined"
          hide-details
          density="compact"
        ></v-select>
      </v-col>
      <v-col cols="6">
        <date-picker v-model="date"></date-picker>
      </v-col>
    </v-row>

    <div class="mt-2 mb-4" v-if="existingDiary">
      <div class="existing-error-message">
        {{ selectedClassName }}
        {{ formattedDate }} {{ $t('classDiary.alreadyExists') }}
        <a
          class="ml-4"
          :href="`/class-diaries/${existingDiary.classId}/${existingDiary.classDiaryId}`"
          >{{ $t('classDiary.goToEditPage') }}</a
        >
      </div>
    </div>

    <v-row>
      <v-col>
        <h3 class="mb-2">{{ $t('classDiary.objective') }}</h3>
        <v-textarea variant="outlined" v-model="aim"></v-textarea>
      </v-col>
      <v-col> </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h3 class="mb-2">{{ $t('classDiary.activitiesMorning') }}</h3>
        <v-textarea variant="outlined" v-model="activityAm"></v-textarea>
      </v-col>
      <v-col>
        <h3 class="mb-2">{{ $t('classDiary.activitiesAfternoon') }}</h3>
        <v-textarea variant="outlined" v-model="activityPm"></v-textarea>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <h3 class="mb-2">{{ $t('classDiary.evaluationReflection') }}</h3>
        <v-textarea variant="outlined" v-model="assessment"></v-textarea>
      </v-col>
      <v-col>
        <h3 class="mb-2">{{ $t('classDiary.parentSupport') }}</h3>
        <v-textarea variant="outlined" v-model="support"></v-textarea>
      </v-col>
    </v-row>

    <div>
      <v-progress-linear
        v-if="isSaving"
        indeterminate
        color="primary"
        class="my-8"
      ></v-progress-linear>
    </div>

    <v-row>
      <v-spacer></v-spacer>
      <v-select
        variant="outlined"
        :label="$t('schoolDiary.applicant')"
        :items="staffOptions"
        item-value="userId"
        item-title="name"
        v-model="appliedBy"
      ></v-select>
      <v-spacer></v-spacer>
    </v-row>

    <v-row>
      <v-spacer></v-spacer>
      <v-btn
        color="primary"
        variant="outlined"
        x-large
        :disabled="(!isContentValid && !isSaving) || existingDiary"
        class="mx-2"
        @click="createAsDraft"
        >{{ $t('schoolDiary.saveAsDraft') }}</v-btn
      >
      <v-btn
        color="primary"
        variant="flat"
        x-large
        :disabled="
          (!isContentValid && !isSaving) || existingDiary || !appliedBy
        "
        class="mx-2"
        @click="createAndApply"
        >{{ $t('schoolDiary.saveAndApply') }}</v-btn
      >
      <v-spacer></v-spacer>
    </v-row>
  </div>
</template>
<script lang="ts">
import { defineComponent } from "vue";

import DatePicker from "@/components/atoms/DatePicker.vue";
import { ClassDiaryResponse, NurseryClassResponse } from "chaild-api/lib";
import { classModule } from "@/store/dataModules/classModule";
import dayjs from "dayjs";
import { classDiaryModle } from "@/store/dataModules/classDiaryModue";
import ApiClassDiary from "@/api/ApiClassDiary";
import LocalDataService from "@/service/LocalDataService";
import { nurseryUserModule } from "@/store/dataModules/nurseryUserModule";
import { PrimaryColumn } from "typeorm";

interface DataType {
  classId: number | null;
  date: string;
  aim: string;
  activityAm: string;
  activityPm: string;
  assessment: string;
  support: string;
  appliedBy: number | null;

  isSaving: boolean;

  existingError: boolean;
  existingDiary: ClassDiaryResponse | null;
}

interface BreadcrumbItem {
  title: string;
  disabled: boolean;
  href: string;
  color: string;
}

export default defineComponent({
  components: {
    DatePicker,
  },
  computed: {
    breadcrumbs(): Array<BreadcrumbItem> {
      return [
        {
          title: this.$t('classDiary.classDiaryList'),
          href: "/class-diaries",
          disabled: false,
          color: "primary"
        },
        {
          title: this.$t('classDiary.createNew'),
          href: "",
          disabled: true,
          color: "grey"
        },
      ];
    },
    classes(): NurseryClassResponse[] {
      return classModule.classes;
    },
    isContentValid(): boolean {
      if (this.date && this.date.trim().length > 0) {
        return true;
      }
      return false;
    },
    selectedClassName(): string {
      if (this.classId) {
        const find = this.classes.find((c) => c.classId === this.classId);
        if (find) {
          return find.className;
        }
      }
      return "";
    },
    formattedDate() {
      return dayjs(this.date).format("YYYY-MM-DD");
    },
    staffOptions() {
      return nurseryUserModule.staffs.filter(s => !s.isHidden).map((staff) => {
        return {
          userId: staff.userId,
          name: `${staff.lastName} ${staff.firstName}`,
        };
      });
    },
  },

  methods: {
    async createAndApply() {
      if (this.classId && this.appliedBy) {
        this.isSaving = true;
        await classDiaryModle.createDiary({
          classId: this.classId,
          date: this.date,
          aim: this.aim,
          activityAm: this.activityAm,
          activityPm: this.activityPm,
          assessment: this.assessment,
          support: this.support,
          status: "applied",
          appliedBy: this.appliedBy,
        });
        this.isSaving = false;

        this.$router.push({ name: "ClassDiaries" });
      }
    },
    async createAsDraft() {
      if (this.classId) {
        this.isSaving = true;
        await classDiaryModle.createDiary({
          classId: this.classId,
          date: this.date,
          aim: this.aim,
          activityAm: this.activityAm,
          activityPm: this.activityPm,
          assessment: this.assessment,
          support: this.support,
          status: "draft",
        });
        this.isSaving = false;

        this.$router.push({ name: "ClassDiaries" });
      }
    },
    async checkExistance() {
      if (this.classId && this.date) {
        const find = await ApiClassDiary.getClassDiary({
          classId: this.classId,
          date: this.date,
        });
        if (find) {
          this.existingDiary = find;
        } else {
          this.existingDiary = null;
        }
      }
    },
  },

  data: (): DataType => {
    return {
      classId: null,
      date: dayjs().format("YYYY-MM-DD"),
      aim: "",
      activityAm: "",
      activityPm: "",
      assessment: "",
      support: "",
      isSaving: false,
      appliedBy: null,

      existingError: false,
      existingDiary: null,
    };
  },

  watch: {
    /*
    classId: {
      handler() {
        this.checkExistance();
      },
    },
    */
    date: {
      handler() {
        this.checkExistance();
        nurseryUserModule.listStaffs({ date: this.date });
      },
    },
  },

  mounted() {
    const nurseryId = LocalDataService.getNurseryId();
    if (nurseryId) {
      classModule.listClass({
        nurseryId: nurseryId,
      });
      nurseryUserModule.listStaffs({ date: this.date });
    }

    const classId = this.$route.query.classId;
    if (classId) {
      try {
        const parsed = parseInt(classId as string);
        this.classId = parsed;
      } catch {
        return;
      }
    }
  },
});
</script>
<style scoped>
.existing-error-message {
  font-weight: bold;
  color: #e53935;
}
</style>
